import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FiSearch, FiX } from 'react-icons/fi';
import {search} from "../../store/actions/search/search.";


const SearchOverlay = ({ isOpen, onClose }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const inputRef = useRef(null);
    const debounceTimerRef = useRef(null);

    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isOpen]);

    useEffect(() => {
        // Clean up on unmount
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (!searchTerm.trim()) {
            setSearchResults([]);
            return;
        }

        // Cancel previous timer
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        // Set new timer
        debounceTimerRef.current = setTimeout(async () => {
            setLoading(true);

            try {
                const results = await dispatch(search(searchTerm.trim()));

                if (results && Array.isArray(results)) {
                    setSearchResults(results);
                } else {
                    setSearchResults([]);
                }
            } catch (error) {
                console.error('Search error:', error);
                setSearchResults([]);
            } finally {
                setLoading(false);
            }
        }, 500); // 500ms debounce
    }, [searchTerm, dispatch]);

    const handleInputChange = (e) => {
        setSearchTerm(e.target.value);
        setSelectedIndex(-1);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            onClose();
        } else if (e.key === 'ArrowDown' && searchResults.length > 0) {
            e.preventDefault();
            setSelectedIndex(prev => (prev < searchResults.length - 1 ? prev + 1 : prev));
        } else if (e.key === 'ArrowUp' && searchResults.length > 0) {
            e.preventDefault();
            setSelectedIndex(prev => (prev > 0 ? prev - 1 : 0));
        } else if (e.key === 'Enter') {
            if (selectedIndex >= 0 && selectedIndex < searchResults.length) {
                handleResultClick(searchResults[selectedIndex]);
            } else if (searchTerm.trim()) {
                handleSearch();
            }
        }
    };

    const handleSearch = () => {
        navigate(`/search?q=${encodeURIComponent(searchTerm.trim())}`);
        onClose();
    };

    const handleCategoryClick = (categoryId, categoryName) => {
        navigate(`/sub-category/${encodeURIComponent(categoryName)}/${categoryId}`, {
            state: {
                categoryData: searchResults.find(item => item.type === 'Category' && item.id === categoryId)
            }
        });
        onClose();
    };

    const handleSubCategoryClick = (subCategoryId, subCategoryName) => {
        navigate(`/product-listing/${encodeURIComponent(subCategoryName)}/${subCategoryId}`);
        onClose();
    };

    const handleProductClick = (productId) => {
        navigate(`/product-detail/${productId}`);
        onClose();
    };

    const handleResultClick = (result) => {
        if (!result || !result.type) {
            navigate(`/search?q=${encodeURIComponent(searchTerm.trim())}`);
            onClose();
            return;
        }

        switch (result.type) {
            case 'Category':
                handleCategoryClick(result.id, result.name);
                break;
            case 'Sub Category':
                handleSubCategoryClick(result.id, result.name);
                break;
            case 'Product':
                handleProductClick(result.id);
                break;
            default:
                navigate(`/search?q=${encodeURIComponent(searchTerm.trim())}`);
                onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <div
            className="search-overlay position-fixed w-100 h-100"
            style={{
                top: 0,
                left: 0,
                backgroundColor: 'rgba(255,255,255,0.95)',
                zIndex: 1050,
                animation: 'fadeIn 0.3s ease'
            }}
        >
            <div className="d-flex justify-content-center align-items-center h-100">
                <div className="search-form-wrapper w-75 w-md-50" style={{ maxWidth: '650px' }}>
                    <button
                        className="search-close position-absolute bg-transparent border-0"
                        style={{
                            top: '30px',
                            right: '30px',
                            fontSize: '24px',
                            cursor: 'pointer',
                            transition: 'transform 0.2s ease',
                            color: '#333'
                        }}
                        onClick={onClose}
                        onMouseOver={(e) => e.currentTarget.style.transform = 'rotate(90deg)'}
                        onMouseOut={(e) => e.currentTarget.style.transform = 'rotate(0)'}
                        aria-label="Close search"
                    >
                        <FiX />
                    </button>
                    <div className="position-relative w-100">
                        <input
                            ref={inputRef}
                            type="text"
                            value={searchTerm}
                            onChange={handleInputChange}
                            onKeyDown={handleKeyDown}
                            className="form-control form-control-lg"
                            placeholder="Search products, categories..."
                            style={{
                                backgroundColor: '#ffffff',
                                borderColor: '#e2e2e2',
                                color: '#333',
                                padding: '15px 20px',
                                fontSize: '16px',
                                borderRadius: '8px',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                            }}
                            autoFocus
                        />
                        <button
                            type="button"
                            onClick={handleSearch}
                            className="btn position-absolute"
                            style={{
                                backgroundColor: '#D4B675',
                                color: '#fff',
                                padding: '8px',
                                borderRadius: '6px',
                                width: '40px',
                                height: '40px',
                                right: '10px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                                transition: 'all 0.2s ease'
                            }}
                            onMouseOver={(e) => {
                                e.currentTarget.style.backgroundColor = '#c0a35f';
                                e.currentTarget.style.transform = 'translateY(-50%) scale(1.05)';
                            }}
                            onMouseOut={(e) => {
                                e.currentTarget.style.backgroundColor = '#D4B675';
                                e.currentTarget.style.transform = 'translateY(-50%)';
                            }}
                        >
                            <FiSearch />
                        </button>
                    </div>

                    {/* Search Results */}
                    {searchTerm && (
                        <div
                            className="search-results mt-4 bg-white rounded p-2"
                            style={{
                                backgroundColor: '#f8f8f8',
                                maxHeight: '450px',
                                overflowY: 'auto',
                                borderRadius: '8px',
                                boxShadow: '0 3px 10px rgba(0,0,0,0.1)'
                            }}
                        >
                            {loading ? (
                                <div className="text-center p-3">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : searchResults.length > 0 ? (
                                <div>
                                    {/* Group results by type */}
                                    {['Category', 'Sub Category', 'Product'].map(type => {
                                        const filteredResults = searchResults.filter(item => item && item.type === type);
                                        if (filteredResults.length === 0) return null;

                                        return (
                                            <div key={type} className="mb-3">
                                                <h6 className="search-category-title mb-2 px-3 py-2"
                                                    style={{
                                                        backgroundColor: '#f0f0f0',
                                                        color: '#555',
                                                        borderRadius: '4px',
                                                        fontWeight: '600',
                                                        fontSize: '14px'
                                                    }}>
                                                    {type}s
                                                </h6>
                                                <ul className="list-group list-group-flush">
                                                    {filteredResults.map((result) => {
                                                        const resultIndex = searchResults.findIndex(r => r && r.id === result.id && r.type === result.type);
                                                        return (
                                                            <li
                                                                key={`${result.type}-${result.id}`}
                                                                className={`list-group-item list-group-item-action border-0 mb-1 ${selectedIndex === resultIndex ? 'bg-light' : ''}`}
                                                                onClick={() => handleResultClick(result)}
                                                                style={{
                                                                    cursor: 'pointer',
                                                                    borderRadius: '6px',
                                                                    transition: 'all 0.2s ease'
                                                                }}
                                                                onMouseOver={(e) => {
                                                                    e.currentTarget.style.backgroundColor = '#f9f9f9';
                                                                }}
                                                                onMouseOut={(e) => {
                                                                    e.currentTarget.style.backgroundColor = selectedIndex === resultIndex ? '#f0f0f0' : '';
                                                                }}
                                                            >
                                                                <div className="d-flex align-items-center">
                                                                    {result.image && (
                                                                        <img
                                                                            src={result.image}
                                                                            alt={result.name}
                                                                            className="me-3"
                                                                            style={{ width: '40px', height: '40px', objectFit: 'contain' }}
                                                                        />
                                                                    )}
                                                                    <div className="flex-grow-1">
                                                                        <div className="fw-bold" style={{ color: '#333' }}>{result.name}</div>
                                                                        {result.type === 'Product' && (
                                                                            <>
                                                                                {result.item_code && (
                                                                                    <small className="text-muted d-block" style={{ fontSize: '12px' }}>Code: {result.item_code}</small>
                                                                                )}
                                                                                {result.description && (
                                                                                    <small className="text-muted d-block text-truncate" style={{ maxWidth: '250px', fontSize: '12px', marginTop: '2px' }}>
                                                                                        {result.description}
                                                                                    </small>
                                                                                )}
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : searchTerm.trim() && !loading ? (
                                <div className="text-center p-3">No results found</div>
                            ) : null}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SearchOverlay;